import {
  CompanyInput,
  type CompanyInputT,
  type RoastResultT,
} from "../schema/roastSchema";
import { ApiError, type RequestRoastOptions } from "../services/apiClient";
import {
  transition,
  initialState,
  type AppState,
  type AppEvent,
} from "./stateMachine";

/**
 * App-level orchestrator that binds the pure state machine to the api client.
 *
 * Holds the current `AppState`, fans out state changes to subscribers, and
 * translates user intents (`submit`, `retry`, `reset`) into a combination of
 * reducer dispatches and injected-api calls.
 *
 * All timing-sensitive dependencies (`now`, `schedule`) are injected so the
 * controller is fully deterministic under fake timers in tests (see
 * Property 3 — "Submission timing and state transitions").
 *
 * See `.kiro/specs/marketing-roast-machine/design.md` — "Services / Modules"
 * and "Runtime Flow".
 */

/**
 * Inline validation errors surfaced on `submit()` when `CompanyInput.safeParse`
 * rejects. Fields are present iff the corresponding input failed validation.
 */
export interface ValidationErrors {
  companyName?: string;
  marketingDescription?: string;
}

/** Listener for state changes. Receives a snapshot of the new state. */
export type StateListener = (state: AppState) => void;

/** Listener for inline validation failures on `submit()`. */
export type ValidationErrorListener = (errors: ValidationErrors) => void;

/**
 * A handle returned by {@link AppControllerDeps.schedule}. Mirrors the shape
 * returned by browser timer wrappers so a matching `cancel` can tear the
 * pending call down.
 */
export interface ScheduleHandle {
  cancel: () => void;
}

/** Injected dependencies for {@link createAppController}. */
export interface AppControllerDeps {
  /** API surface used to request a Roast_Result. */
  api: {
    requestRoast: (
      input: CompanyInputT,
      opts?: RequestRoastOptions,
    ) => Promise<RoastResultT>;
  };
  /** Injectable clock; used to stamp `startedAt` on SUBMIT/RETRY. */
  now: () => number;
  /**
   * Injectable `setTimeout`-equivalent returning a cancellable handle.
   *
   * Reserved for future reveal-orchestration use; the reveal timing
   * currently lives in the ResultScreen component (see task 12.2), and the
   * 60 s client-side watchdog lives in the api client (see `apiClient.ts`).
   * Kept in the deps signature so the controller can pick up timer-driven
   * responsibilities without a breaking change.
   */
  schedule: (fn: () => void, ms: number) => ScheduleHandle;
}

/** Public surface of the controller. */
export interface AppController {
  /** Current state snapshot. */
  getState: () => AppState;
  /**
   * Subscribe to state changes. Returns an unsubscribe function.
   * Listeners receive a snapshot; they must not mutate it.
   */
  subscribe: (listener: StateListener) => () => void;
  /**
   * Subscribe to inline validation errors emitted when `submit()` is called
   * with input that fails `CompanyInput.safeParse`. Returns an unsubscribe
   * function.
   */
  onValidationError: (listener: ValidationErrorListener) => () => void;
  /**
   * Validate and submit visitor input. On invalid input, emits a
   * validation-error event and does NOT call the api. On valid input,
   * dispatches SUBMIT synchronously (so state reaches `loading` within
   * 200 ms of the call — AC 1.7) and kicks off the api request.
   *
   * Submissions made while the app is not `idle` are silently ignored, which
   * debounces double-clicks without surprising the caller.
   */
  submit: (input: { companyName: string; marketingDescription: string }) => void;
  /**
   * Retry the previous submission. Only valid from the `error` state; calls
   * made from any other state are silently ignored. The stored `Company_Input`
   * is replayed byte-for-byte (Property 7).
   */
  retry: () => void;
  /**
   * Return to a fresh `idle` state, cancelling any in-flight request. Safe
   * to call from any state.
   */
  reset: () => void;
  /**
   * Dispatch `REVEAL_TICK` to advance the staggered reveal by one
   * recommendation. Safe to call from any state — the reducer ignores it
   * outside `revealing`. Exposed so the ResultScreen can drive the reveal
   * timeline via the controller rather than mutating state directly.
   */
  advanceReveal: () => void;
  /**
   * Dispatch `SKIP` to flag the reveal as skipped. Safe to call from any
   * state; the reducer only acts while `revealing`. Pair with
   * {@link completeReveal} to actually transition to `result`.
   */
  skipReveal: () => void;
  /**
   * Dispatch `ALL_REVEALED` to transition `revealing → result`. Safe to
   * call from any state — the reducer ignores it outside `revealing`.
   */
  completeReveal: () => void;
}

/**
 * Walk Zod issues and collect the first message for each `Company_Input`
 * field. Only top-level `companyName` / `marketingDescription` paths are
 * considered.
 */
function collectValidationErrors(
  issues: ReadonlyArray<{ path: (string | number)[]; message: string }>,
): ValidationErrors {
  const errors: ValidationErrors = {};
  for (const issue of issues) {
    const head = issue.path[0];
    if (head === "companyName" && errors.companyName === undefined) {
      errors.companyName = issue.message;
    } else if (
      head === "marketingDescription" &&
      errors.marketingDescription === undefined
    ) {
      errors.marketingDescription = issue.message;
    }
  }
  return errors;
}

/**
 * Create a new {@link AppController}.
 *
 * The controller owns its state and fans out changes via a small in-module
 * `Set<StateListener>`. It is safe to instantiate multiple controllers in a
 * single process (e.g. one per test).
 */
export function createAppController(deps: AppControllerDeps): AppController {
  // Hold on to `schedule` so callers that depend on the full deps shape can
  // still inject it; see the JSDoc on `AppControllerDeps.schedule` for the
  // rationale. Marked with `void` to satisfy `noUnusedParameters` without
  // silencing future reveal-orchestration wiring.
  void deps.schedule;

  let state: AppState = initialState;
  const stateListeners = new Set<StateListener>();
  const validationListeners = new Set<ValidationErrorListener>();

  /**
   * Tracks the current in-flight request so `reset()` can cancel it and so
   * late resolutions can detect staleness.
   */
  let pending: AbortController | null = null;

  const dispatch = (event: AppEvent): void => {
    state = transition(state, event);
    // Snapshot iteration so a listener that (un)subscribes during notify
    // doesn't affect the current fan-out.
    for (const listener of Array.from(stateListeners)) {
      listener(state);
    }
  };

  const notifyValidation = (errors: ValidationErrors): void => {
    for (const listener of Array.from(validationListeners)) {
      listener(errors);
    }
  };

  /**
   * Kick off a request for the given input. Handles success and failure by
   * dispatching the appropriate event, but only if our own `AbortController`
   * is still the pending one (i.e. neither `reset()` nor a later submission
   * has superseded us).
   */
  const startRequest = (input: CompanyInputT): void => {
    const ctl = new AbortController();
    pending = ctl;

    deps.api.requestRoast(input, { signal: ctl.signal }).then(
      (result) => {
        if (pending !== ctl) {
          // Superseded by reset() or a later submit — drop the result.
          return;
        }
        pending = null;
        // Only dispatch RESULT_READY while still loading; the reducer would
        // ignore it otherwise, but this keeps listener notifications clean.
        if (state.kind === "loading") {
          dispatch({ type: "RESULT_READY", result });
        }
      },
      (err: unknown) => {
        if (pending !== ctl) {
          return;
        }
        pending = null;
        if (state.kind !== "loading") {
          // State moved on (e.g. via RESET) between promise settlement and
          // this callback — any dispatch would be stale.
          return;
        }
        if (err instanceof ApiError) {
          dispatch({ type: err.kind === "timeout" ? "TIMEOUT" : "ERROR" });
          return;
        }
        // Caller-initiated aborts land here because apiClient re-raises the
        // native AbortError rather than wrapping it. We only reach this
        // branch while still `loading`, which shouldn't happen in practice
        // (reset moves us out first), but be defensive and surface a
        // generic error rather than leaving the app stuck.
        dispatch({ type: "ERROR" });
      },
    );
  };

  const controller: AppController = {
    getState: () => state,

    subscribe: (listener) => {
      stateListeners.add(listener);
      return () => {
        stateListeners.delete(listener);
      };
    },

    onValidationError: (listener) => {
      validationListeners.add(listener);
      return () => {
        validationListeners.delete(listener);
      };
    },

    submit: (raw) => {
      // Debounce double-clicks: only `idle` can start a new submission.
      if (state.kind !== "idle") {
        return;
      }

      const parsed = CompanyInput.safeParse(raw);
      if (!parsed.success) {
        notifyValidation(collectValidationErrors(parsed.error.issues));
        return;
      }

      // Dispatch SUBMIT synchronously — before any await — so state reaches
      // `loading` within the caller's same tick (AC 1.7).
      const input = parsed.data;
      dispatch({ type: "SUBMIT", input, startedAt: deps.now() });
      startRequest(input);
    },

    retry: () => {
      if (state.kind !== "error") {
        return;
      }
      // Capture the input before the reducer moves us to `loading`; after
      // RETRY the new state still carries the same input, but binding it
      // here keeps the call-site obvious.
      const input = state.input;
      dispatch({ type: "RETRY", startedAt: deps.now() });
      startRequest(input);
    },

    reset: () => {
      if (pending) {
        pending.abort();
        pending = null;
      }
      dispatch({ type: "RESET" });
    },

    advanceReveal: () => {
      dispatch({ type: "REVEAL_TICK" });
    },

    skipReveal: () => {
      dispatch({ type: "SKIP" });
    },

    completeReveal: () => {
      dispatch({ type: "ALL_REVEALED" });
    },
  };

  return controller;
}
